import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList, Play, CheckCircle, Clock, Lock, ChevronRight,
  Search, Zap, Award, BarChart2, Filter, RefreshCw
} from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const MicroAssessmentList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | attempted | pending

  useEffect(() => {
    fetchAssessments();
  }, []);

  const fetchAssessments = async () => {
    try {
      setLoading(true);
      const [courseMicro, globalRes] = await Promise.all([
        api.get('/assessments/micro/my-courses').catch(() => ({ data: { success: false, data: [] } })),
        api.get('/assessments?status=active&limit=100').catch(() => ({ data: { success: false, data: [] } })),
      ]);

      const courseItems = courseMicro.data?.success ? courseMicro.data.data || [] : [];
      const globalItems = globalRes.data?.success ? globalRes.data.data || [] : [];
      const seen = new Set();
      const merged = [];
      [...courseItems, ...globalItems.filter((a) => !a.linkedCourse)].forEach((a) => {
        const key = a._id?.toString();
        if (key && !seen.has(key)) {
          seen.add(key);
          merged.push(a);
        }
      });
      setAssessments(merged);
    } catch (err) {
      toast.error('Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  const getUserAttempts = (assessment) => {
    return (assessment.responses || []).filter(
      (r) => r.user?.toString() === user?._id?.toString() || r.user === user?._id
    );
  };

  const getUserAttempt = (assessment) => {
    const attempts = getUserAttempts(assessment);
    return attempts.length ? attempts[attempts.length - 1] : null;
  };

  const filtered = assessments
    .filter((a) => {
      const matchSearch = a.assessmentName.toLowerCase().includes(search.toLowerCase());
      const attempt = getUserAttempt(a);
      if (filter === 'attempted') return matchSearch && !!attempt;
      if (filter === 'pending') return matchSearch && !attempt;
      return matchSearch;
    });

  const stats = {
    total: assessments.length,
    attempted: assessments.filter((a) => !!getUserAttempt(a)).length,
    pending: assessments.filter((a) => !getUserAttempt(a)).length,
    avgScore: (() => {
      const attempts = assessments
        .map((a) => getUserAttempt(a))
        .filter(Boolean)
        .map((r) => r.percentage || 0);
      return attempts.length ? Math.round(attempts.reduce((s, v) => s + v, 0) / attempts.length) : 0;
    })(),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1a3884] to-[#00152E] flex items-center justify-center mx-auto mb-4 animate-pulse shadow-xl">
            <ClipboardList className="w-8 h-8 text-white" />
          </div>
          <p className="text-gray-500 font-medium">Loading assessments…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12">

      {/* ── Hero Header ─────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#00152E] via-[#001f47] to-[#1a3884] p-7 text-white shadow-2xl">
        {/* Decorative circles */}
        <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/5 blur-2xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/3 w-32 h-32 rounded-full bg-blue-400/10 blur-3xl pointer-events-none" />

        <div className="relative flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Zap className="w-4 h-4 text-blue-300 opacity-80" />
              <span className="text-xs font-semibold text-blue-200/80 uppercase tracking-widest">Micro Assessments</span>
            </div>
            <h1 className="text-2xl font-black mb-1">Test Your Knowledge</h1>
            <p className="text-blue-200 text-sm">Quick MCQ quizzes · Instant scores · Randomized questions</p>
          </div>
          <div className="hidden md:grid grid-cols-2 gap-3">
            {[
              { label: 'Available', value: stats.total, color: 'text-white' },
              { label: 'Attempted', value: stats.attempted, color: 'text-green-300' },
              { label: 'Pending', value: stats.pending, color: 'text-amber-300' },
              { label: 'Avg Score', value: `${stats.avgScore}%`, color: 'text-blue-300' },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2.5 text-center border border-white/10">
                <p className={`text-xl font-black ${color}`}>{value}</p>
                <p className="text-[10px] text-white/60 uppercase tracking-wider font-semibold">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Filters ─────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search assessments…"
            className="w-full pl-10 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#1a3884]/30 focus:border-[#1a3884] transition"
          />
        </div>
        <div className="flex gap-2">
          {[
            { key: 'all', label: 'All' },
            { key: 'pending', label: 'Pending' },
            { key: 'attempted', label: 'Attempted' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all border ${filter === key
                ? 'bg-[#1a3884] text-white border-[#1a3884] shadow-md shadow-[#1a3884]/20'
                : 'bg-white text-gray-600 border-gray-200 hover:border-[#1a3884]/40 hover:text-[#1a3884]'
              }`}
            >
              {label}
            </button>
          ))}
          <button
            onClick={fetchAssessments}
            className="p-2.5 bg-white border border-gray-200 rounded-xl text-gray-500 hover:text-[#1a3884] hover:border-[#1a3884]/40 transition"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ── Assessment Cards ─────────────────────────────────── */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center py-20 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
            <ClipboardList className="w-8 h-8 text-gray-300" />
          </div>
          <p className="font-semibold text-gray-600">No assessments found</p>
          <p className="text-sm text-gray-400 mt-1">
            {search ? 'Try a different search term' : 'No active micro assessments available yet'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {filtered.map((assessment) => {
            const attempt = getUserAttempt(assessment);
            const hasAttempted = !!attempt;
            const score = attempt?.percentage ?? null;
            const qCount = assessment.questions?.length || 0;

            return (
              <AssessmentCard
                key={assessment._id}
                assessment={assessment}
                hasAttempted={hasAttempted}
                score={score}
                qCount={qCount}
                onStart={() => navigate(`/student/micro-assessments/${assessment._id}`)}
                onReview={() => navigate(`/student/micro-assessments/${assessment._id}`)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ── Assessment Card ──────────────────────────────────────────── */
const AssessmentCard = ({ assessment, hasAttempted, score, qCount, onStart, onReview }) => {
  const scoreColor =
    score === null ? '' :
    score >= 80 ? 'text-emerald-600' :
    score >= 60 ? 'text-amber-600' : 'text-red-500';

  const ScoreRing = ({ pct }) => {
    const r = 22, circ = 2 * Math.PI * r;
    const offset = circ - (pct / 100) * circ;
    const color = pct >= 80 ? '#10b981' : pct >= 60 ? '#f59e0b' : '#ef4444';
    return (
      <svg width="56" height="56" viewBox="0 0 56 56">
        <circle cx="28" cy="28" r={r} fill="none" stroke="#f1f5f9" strokeWidth="5" />
        <circle cx="28" cy="28" r={r} fill="none" stroke={color} strokeWidth="5"
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round" transform="rotate(-90 28 28)"
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
        <text x="28" y="33" textAnchor="middle" fontSize="11" fontWeight="700" fill={color}>{pct}%</text>
      </svg>
    );
  };

  return (
    <div className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:border-[#1a3884]/20 transition-all duration-300 overflow-hidden flex flex-col">
      {/* Card top accent */}
      <div className={`h-1.5 w-full ${hasAttempted ? 'bg-gradient-to-r from-emerald-400 to-teal-500' : 'bg-gradient-to-r from-[#1a3884] to-blue-500'}`} />

      <div className="p-5 flex flex-col flex-1 gap-4">
        {/* Title row */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full mb-2 ${hasAttempted ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-[#1a3884]'}`}>
              {hasAttempted ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
              {hasAttempted ? 'Completed' : 'Pending'}
            </span>
            <h3 className="font-bold text-gray-900 text-base leading-snug line-clamp-2 group-hover:text-[#1a3884] transition-colors">
              {assessment.courseTitle ? `${assessment.courseTitle}` : assessment.assessmentName}
            </h3>
            {assessment.courseTitle && (
              <p className="text-[10px] text-gray-400 mt-0.5 line-clamp-1">{assessment.assessmentName}</p>
            )}
          </div>
          {hasAttempted && score !== null && (
            <div className="shrink-0">
              <ScoreRing pct={score} />
            </div>
          )}
        </div>

        {/* Description */}
        {assessment.description && (
          <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">
            {assessment.description}
          </p>
        )}

        {/* Meta pills */}
        <div className="flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1 bg-slate-50 text-slate-600 text-xs font-semibold px-2.5 py-1 rounded-full border border-slate-100">
            <BarChart2 className="w-3 h-3" />
            {qCount} Questions
          </span>
          {assessment.randomizeQuestions && (
            <span className="inline-flex items-center gap-1 bg-violet-50 text-violet-600 text-xs font-semibold px-2.5 py-1 rounded-full border border-violet-100">
              <Zap className="w-3 h-3" />
              Randomized
            </span>
          )}
          {assessment.instructions && (
            <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 text-xs font-semibold px-2.5 py-1 rounded-full border border-amber-100">
              {assessment.instructions}
            </span>
          )}
        </div>

        {/* Score breakdown if attempted */}
        {hasAttempted && score !== null && (
          <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
              <span className="font-semibold">Your Score</span>
              <span className={`font-black text-sm ${scoreColor}`}>{score}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div
                className={`h-1.5 rounded-full transition-all ${score >= 80 ? 'bg-emerald-500' : score >= 60 ? 'bg-amber-500' : 'bg-red-500'}`}
                style={{ width: `${score}%` }}
              />
            </div>
          </div>
        )}

        {/* CTA */}
        <button
          onClick={hasAttempted ? onReview : onStart}
          className={`mt-auto w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all shadow-sm ${hasAttempted
            ? 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'
            : 'bg-gradient-to-r from-[#1a3884] to-blue-600 text-white hover:shadow-lg hover:shadow-[#1a3884]/25 hover:-translate-y-0.5'
          }`}
        >
          {hasAttempted ? (
            <><Award className="w-4 h-4 text-emerald-500" /> View Result</>
          ) : (
            <><Play className="w-4 h-4" /> Start Assessment</>
          )}
          <ChevronRight className="w-3.5 h-3.5 opacity-60" />
        </button>
      </div>
    </div>
  );
};

export default MicroAssessmentList;
